import type { EventSink } from '../events'
import { bodyOverlaps } from '../geom'
import {
  advancePlatforms,
  findRiddenPlatform,
  landOnPlatform,
  touchPlatform,
} from '../terrain/platforms'
import type { PlatformDelta } from '../terrain/platforms'
import { snapToRamps } from '../terrain/ramps'
import { feetInAny } from '../terrain/zones'
import { nearBody } from '../track/xindex'
import { NO_TICK } from '../world'
import type { PlayerState, World } from '../world'
import { activeRide, isBurrowed } from './rides'

/**
 * Step 3: move kinematic platforms, carry their riders, then per player integrate X and resolve
 * against solids, integrate Y and resolve against solids, one-way platforms, then ramps.
 * `dys[i]` is player i's vertical displacement from step 2.
 */
export function resolveTerrain(world: World, dys: readonly number[], events: EventSink): void {
  const riders = world.players.map((p) =>
    p.respawnAtTick === NO_TICK ? findRiddenPlatform(world, p) : -1,
  )
  const deltas = advancePlatforms(world)
  for (let i = 0; i < world.players.length; i++) {
    if (world.players[i]!.respawnAtTick !== NO_TICK) continue
    resolvePlayer(world, i, dys[i] ?? 0, riders[i]!, deltas, events)
  }
}

function resolvePlayer(
  world: World,
  i: number,
  dy: number,
  ridden: number,
  deltas: readonly PlatformDelta[],
  events: EventSink,
): void {
  const p = world.players[i]!
  const { track, cfg } = world
  const wasGrounded = p.grounded
  const prevFoot = p.y

  const carry = ridden >= 0 ? deltas[ridden] : undefined
  if (carry) {
    p.x += carry.dx
    p.y += carry.dy
  }

  p.x += p.vx
  resolveX(world, p)

  p.y += dy
  let landed = resolveY(world, p, dy)

  const k = landOnPlatform(world, p, prevFoot, deltas)
  if (k >= 0) {
    p.vy = 0
    landed = true
    touchPlatform(world, k)
  }

  if (snapToRamps(track, p, cfg.rampSnapPx, landed)) {
    p.vy = 0
    landed = true
  }

  let bounced = false
  if (landed && feetInAny(track, p, 'bouncePad')) {
    p.vy = -cfg.bounceVelocityPxPerTick
    p.jumpHeldTicks = 0
    landed = false
    bounced = true
  }

  // A burrowed rider reaching a pit edge pops out with a first jump (D-050).
  if (wasGrounded && !landed && !bounced && isBurrowed(p, world.tick)) {
    p.burrowUntil = world.tick
    p.vy = -cfg.jumpVelocityPxPerTick
    p.jumpsLeft = 1
    p.coyoteUntil = NO_TICK
    p.grounded = false
    events.push({ type: 'jump', tick: world.tick, player: i, double: false })
    return
  }
  p.grounded = landed
  if (landed) p.jumpsLeft = 1
  if (landed && !wasGrounded) events.push({ type: 'land', tick: world.tick, player: i })
  if (bounced) p.coyoteUntil = NO_TICK
  else if (wasGrounded && !landed) p.coyoteUntil = world.tick + cfg.coyoteTicks
}

/**
 * Walls just block: push out against the direction of travel. A grounded player whose feet are
 * at most `rampSnapPx` below a solid's top steps up onto it instead (ramp → ground seams).
 */
function resolveX(world: World, p: PlayerState): void {
  if (p.vx === 0) return
  const { solids, index } = world.track
  const through = bulldozing(world, p)
  for (const k of nearBody(index.solids, p)) {
    const s = solids[k]!
    if ((through && s.obstacle) || !bodyOverlaps(p, s)) continue
    if (p.grounded && p.y - s.y <= world.cfg.rampSnapPx) p.y = s.y
    else p.x = p.vx > 0 ? s.x - p.w : s.x + s.w
  }
}

/** Land on solid tops when moving down, bump heads when moving up. Returns true on landing. */
function resolveY(world: World, p: PlayerState, dy: number): boolean {
  let landed = false
  if (dy === 0) return landed
  const { solids, index } = world.track
  const through = bulldozing(world, p)
  for (const k of nearBody(index.solids, p)) {
    const s = solids[k]!
    if ((through && s.obstacle) || !bodyOverlaps(p, s)) continue
    if (dy > 0) {
      p.y = s.y
      landed = true
    } else {
      p.y = s.y + s.h + p.h
    }
    p.vy = 0
  }
  return landed
}

/** A Wobblebot rider passes through `obstacle` solids (D-050 bulldoze). */
function bulldozing(world: World, p: PlayerState): boolean {
  return activeRide(world, p)?.ability === 'bulldoze'
}
